import { Input, InputNumber, Select } from '@aigc/antd';
import { triggerUpload, useAccept } from '@aigc/hooks';
import { useOverlay } from '@aigc/overlay-hook';
import { tryURL } from '@aigc/toolkit';
import { PlusOutlined } from '@ant-design/icons';
import { useLens } from '@hookform/lenses';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import {
  App,
  Button,
  Form,
  Popconfirm,
  Segmented,
  Upload,
  type UploadFile,
} from 'antd';
import { RESET } from 'jotai/utils';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ClipboardEventHandler,
} from 'react';
import { useForm } from 'react-hook-form';
import { v7 as uuid } from 'uuid';
import type { InferType } from 'yup';
import { FormControl } from '../../../components/form-control';
import { UploadedImageListModal } from '../../../components/uploaded-image-list-modal';
import { LanguageDropdownControl } from '../../../features/common/components/language-dropdown-control';
import { ProductNameTip } from '../../../features/common/components/product-name-tip';
import { Permission } from '../../../features/common/permision';
import { ComplianceDrawer } from '../../../features/compliance/compliance-drawer';
import {
  createBeforeUpload,
  createUploadValidator,
} from '../../../features/file';
import { usePreviewFn } from '../../../features/file/hooks';
import type {
  FileDto,
  UploadFileOutputDto,
} from '../../../features/file/model';
import {
  toFileInputDtoList,
  toFileListFormValues,
} from '../../../features/file/utils';
import {
  DEFAULT_FRAME_IMAGE_MODEL,
  FRAME_IMAGE_MODEL_MAP,
} from '../../../features/frame-image/constants';
import { frameImageSchema } from '../../../features/frame-image/schema';
import {
  useReadFrameImagePageData,
  useSetFrameImagePageData,
} from '../../../features/frame-image/store';
import { useAuthority } from '../../../features/me/hook';
import { saveScript } from '../../../features/script/api';
import { ScriptType } from '../../../features/script/model';
import type { ScriptDto } from '../../../features/script/script.dto';
import {
  TaskType,
  type VideoGenerationTaskDto,
} from '../../../features/task/model';
import { DurationAlert } from '../../../features/video-model/components/duration-alert';
import { DurationTooltip } from '../../../features/video-model/components/duration-tooltip';
import { MultiPromptControl } from '../../../features/video-model/components/multi-prompt-control';
import { PromptControl } from '../../../features/video-model/components/prompt-control';
import {
  GenerationType,
  type VideoModelCode,
} from '../../../features/video-model/model-spec';
import {
  getGenerateVideoTaskMutationOptions,
  translatePromptMutationOptions,
} from '../../../features/video/api';
import { AspectRatioSelect } from '../../../features/video/components/aspect-ratio-select';
import { VideoGenerationTaskTable } from '../../../features/video/components/task-table';
import { useTryParsePrompt } from '../../../features/video/hook';
import type { GenerateVideoInputDto } from '../../../features/video/model';
import { ParseError, parsePrompt } from '../../../features/video/parse';
import {
  buildMultiPrompt,
  scriptTemplate,
} from '../../../features/video/utils';

type FormValues = InferType<typeof frameImageSchema>;

export function VideoCreate() {
  const { message, modal, notification } = App.useApp();
  const [openOverlay] = useOverlay<FileDto>();
  const onPreview = usePreviewFn();
  const [openDrawer] = useOverlay<string>();
  const readPageData = useReadFrameImagePageData();
  const pageData = readPageData();
  const setFrameImagePageData = useSetFrameImagePageData();
  const translate = useMutation(translatePromptMutationOptions);
  const [simpleMode, setSimpleMode] = useState(pageData.simple_mode ?? true);
  const tryParsePrompt = useTryParsePrompt(simpleMode);
  const authority = useAuthority();

  const initialTagRef = useRef(true);
  if (initialTagRef.current) {
    initialTagRef.current = false;
    setFrameImagePageData(pageData);
  }
  if (!pageData.id) {
    setFrameImagePageData({
      ...pageData,
      id: uuid(),
    });
  }

  const form = useForm({
    defaultValues: {
      aspect_ratio: '9:16',
      model: DEFAULT_FRAME_IMAGE_MODEL,
      first_frame: null!,
      end_frame: null,
      duration: 5,
      prompt: '',
      generate_audio: true,
    },
    disabled: translate.isPending,
    resolver: yupResolver(frameImageSchema),
    context: {
      simple_mode: simpleMode,
      generation_type: 'frame',
    },
  });
  const {
    control,
    handleSubmit,
    subscribe,
    reset,
    setValue,
    trigger,
    getFieldState,
    getValues,
    formState: { isValid, isDirty },
  } = form;
  const lens = useLens({ control });

  const generate = useMutation(getGenerateVideoTaskMutationOptions());

  useEffect(() => {
    return subscribe({
      formState: {
        values: true,
      },
      callback: ({ values }) => {
        const { first_frame, end_frame, multi_prompt, extra_prompt, ...rest } =
          values;

        const file_list: UploadFile<UploadFileOutputDto>[] = [];
        if (first_frame) {
          file_list.push(first_frame);
        }
        if (end_frame) {
          file_list.push(end_frame);
        }

        setFrameImagePageData((prevPageData) => {
          return {
            ...prevPageData,
            ...rest,
            prompt:
              (prevPageData.simple_mode ?? true)
                ? (values.prompt ?? '')
                : buildMultiPrompt(values.multi_prompt, values.extra_prompt),
            file_list: toFileInputDtoList(file_list),
          };
        });
      },
    });
  }, [setFrameImagePageData, subscribe]);

  const toFormValues = useCallback(
    (
      data: ScriptDto | undefined,
      model: VideoModelCode,
    ): Partial<FormValues> => {
      const file_list = toFileListFormValues(data?.file_list ?? []);

      return {
        first_frame: file_list.at(0),
        end_frame: file_list.at(1),
        duration: data?.duration,
        aspect_ratio: data?.aspect_ratio,
        product_name: data?.product_name,
        model: model,
        ...tryParsePrompt(data?.prompt ?? ''),
        generate_audio: data?.generate_audio,
      };
    },
    [tryParsePrompt],
  );

  useEffect(() => {
    reset(toFormValues(pageData, DEFAULT_FRAME_IMAGE_MODEL), {
      keepDefaultValues: true,
    });
  }, []);

  const handleGenerateVideo = handleSubmit(
    async (values) => {
      const file_list = toFileInputDtoList([
        ...[values.first_frame, values.end_frame].filter(
          (file) => file != null,
        ),
      ]);

      const requestBody: GenerateVideoInputDto = {
        id: pageData.id,
        template_id: pageData.template_id,
        duration: values.duration,
        generation_type: GenerationType.Frame,
        aspect_ratio: values.aspect_ratio,
        model: values.model,
        prompt: values.prompt ?? '',
        file_list,
        product_name: values.product_name,
        // TODO: 这些字段都不是用户填写的，应该由后端自己从数据库获取
        resolution: '720p',
        size: values.aspect_ratio === '16:9' ? '1280x720' : '720x1280',
        filename: pageData.filename,
        product_description: pageData.product_description,
        product_image_filename: pageData.product_image_filename,
        target_region: pageData.target_region,
        avatar_ids: pageData.avatar_ids,
        simple_mode: simpleMode,
        source: pageData.source,
        generate_audio: values.generate_audio,
      };

      lfq('track', 'video.generate.frame_image');
      const data = await generate.mutateAsync(requestBody);

      message.success(`视频生成任务已提交！Task ID: ${data.id}`);
    },
    (error) => {
      if (error.prompt?.root?.message) {
        notification.error({
          title: '错误',
          description: error.prompt.root.message,
        });
      }
    },
  );

  const onCheckPrompt = async () => {
    const prompt = simpleMode
      ? (getValues('prompt') ?? '')
      : buildMultiPrompt(getValues('multi_prompt'), getValues('extra_prompt'));

    if (!prompt.trim().length) {
      message.info('请输入提示词');
      return;
    }

    const overlayRef = openDrawer(
      <ComplianceDrawer prompt={prompt}></ComplianceDrawer>,
    );
    const newPrompt = await overlayRef.afterClosed;

    if (newPrompt) {
      try {
        if (simpleMode) {
          setValue('prompt', newPrompt);
        } else {
          const { extra_prompt, multi_prompt: segments } =
            parsePrompt(newPrompt);
          setValue('multi_prompt', segments);
          setValue('extra_prompt', extra_prompt);
        }
      } catch (error) {
        if (error instanceof ParseError) {
          setSimpleMode(true);
          setValue('prompt', newPrompt);
          return;
        }

        throw error;
      }
    }
  };

  const handleSaveScript = async (data: ScriptDto) => {
    lfq('track', 'script.save');

    await trigger();
    if (!isValid) {
      notification.error({
        title: '错误',
        description: '表单校验失败，请修改表单数据',
      });
      return;
    }

    await saveScript({
      type: ScriptType.Video,
      tags: null,
      data: {
        ...data,
        generation_type: GenerationType.Frame,
      },
    });

    message.success('脚本保存成功！');
  };

  const handleTranslate = async (lang: string) => {
    trigger();

    const ok = await new Promise<boolean>((resolve) => {
      setTimeout(() => {
        if (getFieldState('prompt').invalid) {
          notification.error({
            title: '错误',
            description: '请修正脚本配置的错误后重试',
          });
          return resolve(false);
        }

        return resolve(true);
      }, 0);
    });

    if (!ok) {
      return;
    }

    const loadingKey = uuid();

    try {
      message.open({
        key: loadingKey,
        type: 'loading',
        content: '正在为你设置语种，请稍候',
      });
      const result = await translate.mutateAsync({
        prompt: simpleMode
          ? (getValues('prompt') ?? '')
          : buildMultiPrompt(
              getValues('multi_prompt'),
              getValues('extra_prompt'),
            ),
        target_language: lang,
      });

      message.success(`“旁白/对话/字幕”语种已更改为“${lang}”`);

      if (simpleMode) {
        setValue('prompt', result.prompt);
      } else {
        try {
          const { extra_prompt, multi_prompt: segments } = parsePrompt(
            result.prompt,
          );
          setValue('extra_prompt', extra_prompt);
          setValue('multi_prompt', segments);
        } catch (error) {
          if (error instanceof ParseError) {
            setSimpleMode(true);
            setValue('prompt', result.prompt);
            return;
          }
          throw error;
        }
      }
    } finally {
      message.destroy(loadingKey);
    }
  };

  const accept = useAccept(['.jpg', '.jpeg', '.png', '.webp']);
  useEffect(() => {
    const cb: ClipboardEventHandler = (ev) => {
      const item = ev.clipboardData.files.item(0);

      if (!item) {
        return;
      }

      if (!accept.filter(item)) {
        return;
      }

      if (!getValues('first_frame') && triggerUpload('#firstUploader', item)) {
        ev.preventDefault();
        return;
      }

      if (!getValues('end_frame') && triggerUpload('#lastUploader', item)) {
        ev.preventDefault();
        return;
      }
    };

    document.addEventListener('paste', cb as any);

    return () => {
      document.removeEventListener('paste', cb as any);
    };
  }, [accept, getValues]);

  return (
    <div className="flex w-full flex-col divide-y divide-primary-200 overflow-y-auto sm:h-full sm:flex-row sm:divide-x sm:divide-y-0">
      {/* 左右布局，左侧操作面板，占据60% */}
      <div className="card animate-fade-in sm:overflow-auto xl:w-[480px]">
        <Form layout="vertical">
          <FormControl
            control={control}
            name="product_name"
            label="产品名称"
            required
            render={({ field }) => (
              <Input {...field} placeholder="请输入产品名称"></Input>
            )}
            extra={<ProductNameTip></ProductNameTip>}
          ></FormControl>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-2">
            <FormControl
              control={control}
              name="first_frame"
              label="首帧图片"
              render={({ field }) => {
                return (
                  <>
                    <Button
                      className="mb-2"
                      variant="filled"
                      color="default"
                      block
                      disabled={field.disabled}
                      onClick={async () => {
                        const overlayRef = openOverlay(
                          <UploadedImageListModal></UploadedImageListModal>,
                        );

                        const file = await overlayRef.afterClosed;

                        if (!file) {
                          return;
                        }

                        const url = tryURL(file.oss_object_key, file.oss_host);

                        if (!url) {
                          return;
                        }

                        setValue('first_frame', {
                          uid: uuid(),
                          name: file.filename,
                          url: url.toString(),
                          thumbUrl: url.toString(),
                          status: 'done',
                          response: {
                            id: file.id,
                            filename: file.filename,
                            mime: file.mime,
                            file_url: url.toString(),
                            created_at: file.created_at,
                          },
                        });
                      }}
                    >
                      从图片库选择
                    </Button>
                    <Upload<UploadFileOutputDto>
                      id="firstUploader"
                      styles={{
                        root: {
                          '--ant-upload-picture-card-size': '100%',
                        },
                      }}
                      classNames={{
                        trigger: 'aspect-square',
                        list: 'aspect-square',
                      }}
                      name="avatar"
                      disabled={field.disabled}
                      listType="picture-card"
                      accept={accept}
                      beforeUpload={createBeforeUpload({
                        validator: createUploadValidator({
                          accept: accept.format,
                        }),
                        onError: ({ title, message }) => {
                          notification.error({ title, description: message });
                        },
                      })}
                      fileList={field.value != null ? [field.value] : []}
                      onPreview={onPreview}
                      onChange={(info) => {
                        const { status } = info.file;

                        if (status === 'error') {
                          notification.error({
                            title: '错误',
                            description: `图片 ${info.file.name} 上传失败，请重试`,
                          });
                        }

                        field.onChange(
                          info.fileList
                            .map((item) => {
                              if (item.response) {
                                item.url = item.response.file_url;
                                item.thumbUrl = item.response.file_url;
                              }

                              return item;
                            })
                            .at(0),
                        );
                      }}
                    >
                      {field.value == null && (
                        <div>
                          <PlusOutlined />
                          <div className="mt-2">
                            点击、拖放或
                            <span className="text-blue-500">粘贴</span>上传
                          </div>
                        </div>
                      )}
                    </Upload>
                  </>
                );
              }}
            ></FormControl>
            <FormControl
              control={control}
              name="end_frame"
              label="尾帧图片"
              render={({ field }) => (
                <>
                  <Button
                    className="mb-2"
                    variant="filled"
                    color="default"
                    block
                    disabled={field.disabled}
                    onClick={async () => {
                      const overlayRef = openOverlay(
                        <UploadedImageListModal></UploadedImageListModal>,
                      );

                      const file = await overlayRef.afterClosed;

                      if (!file) {
                        return;
                      }

                      const url = tryURL(file.oss_object_key, file.oss_host);

                      if (!url) {
                        return;
                      }

                      setValue('end_frame', {
                        uid: uuid(),
                        name: file.filename,
                        url: url.toString(),
                        thumbUrl: url.toString(),
                        status: 'done',
                        response: {
                          id: file.id,
                          filename: file.filename,
                          mime: file.mime,
                          file_url: url.toString(),
                          created_at: file.created_at,
                        },
                      });
                    }}
                  >
                    从图片库选择
                  </Button>
                  <Upload<UploadFileOutputDto>
                    id="lastUploader"
                    styles={{
                      root: {
                        '--ant-upload-picture-card-size': '100%',
                      },
                    }}
                    classNames={{
                      trigger: 'w-full block aspect-square',
                      list: 'aspect-square',
                    }}
                    name="avatar"
                    listType="picture-card"
                    showUploadList={{
                      showRemoveIcon: true,
                    }}
                    disabled={field.disabled}
                    fileList={field.value != null ? [field.value] : []}
                    accept={accept}
                    beforeUpload={createBeforeUpload({
                      validator: createUploadValidator({
                        accept: accept.format,
                      }),
                      onError: ({ title, message }) => {
                        notification.error({ title, description: message });
                      },
                    })}
                    onPreview={onPreview}
                    onChange={(info) => {
                      const { status } = info.file;

                      if (status === 'error') {
                        notification.error({
                          title: '错误',
                          description: `图片 ${info.file.name} 上传失败，请重试`,
                        });
                      }

                      field.onChange(
                        info.fileList
                          .map((item) => {
                            if (item.response) {
                              item.url = item.response.file_url;
                              item.thumbUrl = item.response.file_url;
                            }

                            return item;
                          })
                          .at(0),
                      );
                    }}
                  >
                    {field.value == null && (
                      <div>
                        <PlusOutlined />
                        <div className="mt-2">
                          点击、拖放或
                          <span className="text-blue-500">粘贴</span>上传
                        </div>
                      </div>
                    )}
                  </Upload>
                </>
              )}
            ></FormControl>
          </div>
        </Form>
        <Form
          layout="vertical"
          classNames={{
            root: 'grid grid-cols-1 sm:grid-cols-2 gap-x-4 mt-4',
          }}
        >
          <FormControl
            control={control}
            name="model"
            label="生成平台"
            render={({ field }) => (
              <Select
                {...field}
                allowClear={false}
                popupMatchSelectWidth={false}
                options={FRAME_IMAGE_MODEL_MAP.entries()
                  .filter(([_, spec]) => authority(spec.permission))
                  .map(([code, spec]) => ({
                    label: `${spec.name}(${spec.credit})`,
                    value: code,
                  }))
                  .toArray()}
                onChange={field.onChange}
              ></Select>
            )}
          ></FormControl>
          <FormControl
            control={control}
            name="aspect_ratio"
            label="宽高比"
            render={({ field }) => (
              <AspectRatioSelect {...field}></AspectRatioSelect>
            )}
          ></FormControl>
          <FormControl
            control={control}
            name="duration"
            label="时长"
            tooltip={{
              classNames: { root: 'min-w-fit' },
              title: (
                <DurationTooltip
                  generationType={GenerationType.Frame}
                ></DurationTooltip>
              ),
            }}
            render={({ field }) => (
              <InputNumber
                classNames={{ root: 'w-full' }}
                {...field}
                min={0}
                precision={0}
                suffix="秒"
              ></InputNumber>
            )}
          ></FormControl>
          <FormControl
            control={control}
            name="generate_audio"
            label="输出声音"
            render={({ field }) => {
              return (
                <Segmented
                  options={[
                    { value: true, label: '有声' },
                    { value: false, label: '无声' },
                  ]}
                  {...field}
                ></Segmented>
              );
            }}
          ></FormControl>
        </Form>
        <div className="sticky bottom-0 flex space-x-2">
          <Popconfirm
            title="确定要重置吗？"
            onConfirm={() => {
              reset();
              setFrameImagePageData(RESET);
            }}
          >
            <Button size="large" variant="solid" color="primary">
              重置
            </Button>
          </Popconfirm>
          <Popconfirm
            title="确定要生成视频吗？"
            onConfirm={handleGenerateVideo}
          >
            <Button
              variant="solid"
              color="primary"
              size="large"
              loading={generate.isPending}
              block
              disabled={translate.isPending}
            >
              {generate.isPending ? '生成中...' : '✨ 生成视频'}
            </Button>
          </Popconfirm>
          <Button
            variant="solid"
            color="primary"
            size="large"
            onClick={onCheckPrompt}
          >
            敏感内容检测
          </Button>
        </div>
        <VideoGenerationTaskTable
          taskType={[TaskType.视频生成]}
          scriptId={pageData.id}
          onRowClick={async (task) => {
            const data = (task as VideoGenerationTaskDto).generate_data;

            if (isDirty) {
              const confirm = await modal.confirm({
                title: '查看脚本',
                content: '确定展示该历史脚本？该操作会覆盖当前脚本',
              });

              if (!confirm) {
                return;
              }
            }
            reset(toFormValues(data, getValues('model')));
          }}
        ></VideoGenerationTaskTable>
      </div>

      <div className="card flex-auto animate-slide-up space-y-6 sm:min-h-40 sm:overflow-auto xl:flex-1">
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Button
              variant="solid"
              color="primary"
              onClick={() => {
                handleSaveScript(pageData);
              }}
            >
              保存脚本
            </Button>
            <Segmented<number>
              value={+!!simpleMode}
              options={[
                { label: '简易模式', value: 1 },
                { label: '自定义分镜', value: 0 },
              ]}
              onChange={async (value) => {
                try {
                  if (value) {
                    setValue(
                      'prompt',
                      buildMultiPrompt(
                        getValues('multi_prompt'),
                        getValues('extra_prompt'),
                      ),
                    );
                  } else {
                    const { extra_prompt, multi_prompt: segments } =
                      parsePrompt(getValues('prompt'));
                    setValue('multi_prompt', segments);
                    setValue('extra_prompt', extra_prompt);
                  }
                } catch (error) {
                  if (error instanceof ParseError) {
                    notification.error({
                      title: '无法切换自定义分镜',
                      description: `提示词解析失败${error.message}`,
                    });
                    return;
                  }

                  throw error;
                }

                setSimpleMode(!!value);
                setFrameImagePageData((prevPageData) => {
                  return {
                    ...prevPageData,
                    simple_mode: !!value,
                  };
                });
              }}
            />
            {simpleMode && (
              <Button
                variant="filled"
                color="default"
                onClick={() => {
                  const value = getValues('prompt');
                  if (value?.length) {
                    modal.confirm({
                      title: '初始化脚本',
                      content: '初始化脚本会清空现有脚本内容，确定要初始化吗？',
                      onOk: () => {
                        setValue('prompt', scriptTemplate);
                      },
                    });

                    return;
                  }

                  setValue('prompt', scriptTemplate);
                }}
              >
                初始化脚本
              </Button>
            )}
            <div className="flex-auto"></div>
            <LanguageDropdownControl
              isLoading={translate.isPending}
              onTranslate={handleTranslate}
            ></LanguageDropdownControl>
          </div>
          <DurationAlert></DurationAlert>
          {simpleMode ? (
            <PromptControl
              generationType={GenerationType.Frame}
              modelLens={lens.focus('model')}
              lens={lens.focus('prompt')}
            ></PromptControl>
          ) : (
            <MultiPromptControl
              generationType={GenerationType.Frame}
              lens={lens.focus('multi_prompt').cast()}
            ></MultiPromptControl>
          )}
          {!simpleMode && (
            <FormControl
              layout="vertical"
              control={control}
              name="extra_prompt"
              label="额外说明"
              render={({ field }) => (
                <Input.TextArea
                  {...field}
                  value={field.value ?? ''}
                  rows={4}
                ></Input.TextArea>
              )}
            ></FormControl>
          )}
        </div>
      </div>
    </div>
  );
}

export const Route = createFileRoute('/_creator-center/video/create')({
  staticData: { permissions: [Permission.视频生成] },
  component: VideoCreate,
});
